import React, { useState, useRef } from 'react';
import { User, GraduationCap, Clock, Users, Send, CheckSquare, Square, Building2, MapPin, Briefcase, FolderOpen, Sparkles, X, Loader2, FolderInput, BarChart2, ChevronDown, Search, Zap, Star, UserCheck } from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { Candidate, Position, STAGE_LABELS, PIPELINE_STAGES, CandidateStage, DISPLAY_STAGE_GROUPS } from '../types';
import { aiSearch, batchAssignCandidates, aiAnalyzeResume, createEvaluation, aiScoreFeedback, favoriteCandidate, unfavoriteCandidate } from '../api';

interface Props {
  candidates: Candidate[];
  onSelect: (id: string) => void;
  positionName?: string;
  currentPositionId?: string;
  isHr?: boolean;
  onFeishuNotify?: (candidates: Candidate[]) => void;
  onUpdate?: () => void;
  notificationBell?: React.ReactNode;
  onStageChange?: (candidateIds: string[]) => void;
  positions?: Position[];
  onSearch?: (query: string) => void;
  searchQuery?: string;
  favoritesOnly?: boolean;
  onFavoritesOnlyChange?: (v: boolean) => void;
  reusableOnly?: boolean;
  onReusableOnlyChange?: (v: boolean) => void;
  initialStage?: CandidateStage;
}

const stageDotColors: Record<string, string> = {
  new: 'bg-gray-400',
  screening: 'bg-primary-300',
  screening_passed: 'bg-primary-400',
  screening_rejected: 'bg-gray-400',
  first_interview: 'bg-primary-400',
  first_interview_rejected: 'bg-gray-400',
  second_interview: 'bg-primary-500',
  second_interview_rejected: 'bg-gray-500',
  final_interview: 'bg-primary-500',
  final_interview_rejected: 'bg-gray-500',
  offer: 'bg-primary-600',
  offer_rejected: 'bg-gray-500',
  pending_onboard: 'bg-primary-600',
  onboarded: 'bg-primary-700',
};

const stageActiveColors: Record<string, string> = {
  new: 'text-gray-800 bg-gray-100/80',
  screening: 'text-primary-700 bg-primary-50/80',
  screening_passed: 'text-primary-700 bg-primary-50/80',
  screening_rejected: 'text-gray-600 bg-gray-100/80',
  first_interview: 'text-primary-700 bg-primary-50/80',
  first_interview_rejected: 'text-gray-600 bg-gray-100/80',
  second_interview: 'text-primary-700 bg-primary-50/80',
  second_interview_rejected: 'text-gray-600 bg-gray-100/80',
  final_interview: 'text-primary-700 bg-primary-50/80',
  final_interview_rejected: 'text-gray-600 bg-gray-100/80',
  offer: 'text-primary-700 bg-primary-50/80',
  offer_rejected: 'text-gray-600 bg-gray-100/80',
  pending_onboard: 'text-primary-700 bg-primary-50/80',
  onboarded: 'text-primary-700 bg-primary-50/80',
};

const stageCountColors: Record<string, string> = {
  new: 'bg-gray-200/60 text-gray-600',
  screening: 'bg-primary-100/80 text-primary-600',
  screening_passed: 'bg-primary-100/80 text-primary-600',
  screening_rejected: 'bg-gray-200/70 text-gray-500',
  first_interview: 'bg-primary-100/80 text-primary-600',
  first_interview_rejected: 'bg-gray-200/70 text-gray-500',
  second_interview: 'bg-primary-100/80 text-primary-600',
  second_interview_rejected: 'bg-gray-200/70 text-gray-500',
  final_interview: 'bg-primary-100/80 text-primary-600',
  final_interview_rejected: 'bg-gray-200/70 text-gray-500',
  offer: 'bg-primary-100/80 text-primary-600',
  offer_rejected: 'bg-gray-200/70 text-gray-500',
  pending_onboard: 'bg-primary-100/80 text-primary-600',
  onboarded: 'bg-primary-100/80 text-primary-600',
};

export default function CandidateCards({ candidates, onSelect, positionName, currentPositionId, isHr, onFeishuNotify, onUpdate, notificationBell, onStageChange, positions, onSearch, searchQuery = '', favoritesOnly, onFavoritesOnlyChange, reusableOnly, onReusableOnlyChange, initialStage }: Props) {
  const [localSearch, setLocalSearch] = React.useState(searchQuery);
  const initialGroup = initialStage ? DISPLAY_STAGE_GROUPS.find(g => (g.stages as string[]).includes(initialStage)) : undefined;
  const [activeGroupKey, setActiveGroupKey] = useState<string>(initialGroup?.key || DISPLAY_STAGE_GROUPS[0].key);
  const [activeSubCategory, setActiveSubCategory] = useState<string | null>(
    initialGroup?.subCategories ? (initialStage as string) : null
  );
  // 外部指定初始阶段（如首页「Offer中」卡跳转）变化时重新定位分组/子分类
  React.useEffect(() => {
    if (!initialStage) return;
    const g = DISPLAY_STAGE_GROUPS.find(grp => (grp.stages as string[]).includes(initialStage));
    if (!g) return;
    setActiveGroupKey(g.key);
    setActiveSubCategory(g.subCategories ? (initialStage as string) : null);
  }, [initialStage]);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // AI search
  const [aiQuery, setAiQuery] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiAnswer, setAiAnswer] = useState('');
  const [aiCandidates, setAiCandidates] = useState<any[]>([]);
  const [aiOpen, setAiOpen] = useState(false);
  const aiInputRef = useRef<HTMLInputElement>(null);
  const [showAssignDropdown, setShowAssignDropdown] = useState(false);
  const assignRef = useRef<HTMLDivElement>(null);
  // Batch AI scoring
  const [feedbackOpen, setFeedbackOpen] = useState<string | null>(null); // candidateId with open feedback
  const [aiScoring, setAiScoring] = useState(false);
  const [aiScoreProgress, setAiScoreProgress] = useState(0);
  const [aiScoringTotal, setAiScoringTotal] = useState(0);
  const [aiScores, setAiScores] = useState<Record<string, number>>({}); // candidateId -> score
  const [aiSortActive, setAiSortActive] = useState(false);
  // Favorites
  const [favSet, setFavSet] = useState<Set<string>>(new Set());
  const [favLocalOnly, setFavLocalOnly] = useState(false);
  const showFavoritesOnly = favoritesOnly !== undefined ? favoritesOnly : favLocalOnly;
  const setShowFavoritesOnly = (v: boolean) => { onFavoritesOnlyChange ? onFavoritesOnlyChange(v) : setFavLocalOnly(v); };
  // 回捞视图：淘汰候选人里「非技术型淘汰」的可再次激活部分
  const [reusableLocalOnly, setReusableLocalOnly] = useState(false);
  const showReusableOnly = reusableOnly !== undefined ? reusableOnly : reusableLocalOnly;
  const setShowReusableOnly = (v: boolean) => { onReusableOnlyChange ? onReusableOnlyChange(v) : setReusableLocalOnly(v); };
  const isReusableCandidate = (c: Candidate) => c.stage?.includes('rejected') && !!c.reject_reason && c.reject_reason !== 'ability';

  React.useEffect(() => { setLocalSearch(searchQuery); }, [searchQuery]);

  // Seed favorites from candidates' is_favorite
  React.useEffect(() => {
    setFavSet(new Set(candidates.filter(c => c.is_favorite).map(c => c.id)));
  }, [candidates]);

  const toggleFavorite = async (id: string) => {
    const willFav = !favSet.has(id);
    setFavSet(prev => {
      const next = new Set(prev);
      if (willFav) next.add(id); else next.delete(id);
      return next;
    });
    try {
      if (willFav) { await favoriteCandidate(id); }
      else { await unfavoriteCandidate(id); }
    } catch {
      // revert on failure
      setFavSet(prev => {
        const next = new Set(prev);
        if (willFav) next.delete(id); else next.add(id);
        return next;
      });
      toast.error('操作失败');
    }
  };

  // Unified search: phone/email/short name → keyword; longer natural language → AI
  const isKeywordQuery = (q: string) => {
    const t = q.trim();
    if (!t) return true;
    if (/^[\d\s+\-()]{6,}$/.test(t)) return true; // phone-like
    if (t.includes('@')) return true;               // email
    if (t.length <= 6 && !/\s/.test(t)) return true; // short name
    return false;
  };

  const handleUnifiedSearch = (q: string) => {
    const trimmed = q.trim();
    if (!trimmed) {
      onSearch?.('');
      setAiQuery('');
      setAiOpen(false);
      setAiAnswer('');
      setAiCandidates([]);
      return;
    }
    if (isKeywordQuery(trimmed)) {
      onSearch?.(trimmed);
      setAiQuery('');
      setAiOpen(false);
    } else {
      setAiQuery(trimmed);
      onSearch?.('');
      // trigger AI search
      setAiLoading(true);
      setAiAnswer('');
      setAiCandidates([]);
      setAiOpen(true);
      aiSearch(trimmed)
        .then(data => { setAiAnswer(data.answer || '未获取到结果'); setAiCandidates(data.candidates || []); })
        .catch(err => { setAiAnswer(err.response?.data?.message || 'AI搜索出错'); })
        .finally(() => setAiLoading(false));
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    handleUnifiedSearch(localSearch);
  };

  const clearSearch = () => {
    setLocalSearch('');
    onSearch?.('');
    setAiQuery('');
    setAiOpen(false);
    setAiAnswer('');
    setAiCandidates([]);
  };

  // Group candidates by stage
  // 收藏视图下统计条只统计收藏的候选人，避免展示整个人才池的阶段数量
  const grouped: Record<string, Candidate[]> = {};
  for (const stage of PIPELINE_STAGES) grouped[stage] = [];
  const listSource = showReusableOnly ? candidates.filter(isReusableCandidate) : (showFavoritesOnly ? candidates.filter(c => favSet.has(c.id)) : candidates);
  for (const c of listSource) {
    if (grouped[c.stage]) grouped[c.stage].push(c);
    else grouped['new'].push(c);
  }

  // Get current active group
  const activeGroup = DISPLAY_STAGE_GROUPS.find(g => g.key === activeGroupKey) || DISPLAY_STAGE_GROUPS[0];
  
  // Get current items based on active sub-category or all stages in group
  // 我的收藏 / 回捞池：忽略阶段分组，直接展示该视图下的全部候选人
  const baseItems = showReusableOnly
    ? candidates.filter(isReusableCandidate)
    : showFavoritesOnly
    ? candidates.filter(c => favSet.has(c.id))
    : activeSubCategory
      ? (grouped[activeSubCategory] || [])
      : activeGroup.stages.flatMap(stage => grouped[stage] || []);
  // Sort by AI score when active
  const currentItems = aiSortActive && Object.keys(aiScores).length > 0
    ? [...baseItems].sort((a, b) => (aiScores[b.id] ?? 0) - (aiScores[a.id] ?? 0))
    : baseItems;

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    if (selectedIds.size === currentItems.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(currentItems.map(c => c.id)));
    }
  };

  const exitSelectMode = () => { setSelectMode(false); setSelectedIds(new Set()); setShowAssignDropdown(false); };

  const handleBatchAiScore = async () => {
    if (aiScoring || currentItems.length === 0) return;
    // Snapshot the list at the moment the button is clicked — currentItems is reactive
    const snapshot = [...currentItems];
    const total = snapshot.length;

    const currentPos = positions?.find(p => p.id === currentPositionId);
    let posJd = currentPos
      ? [currentPos.description, currentPos.requirements ? `任职要求：\n${currentPos.requirements}` : ''].filter(Boolean).join('\n\n')
      : '';
    if (!posJd && currentPos?.name) posJd = `应聘岗位：${currentPos.name}`;
    if (!posJd && positionName) posJd = `应聘岗位：${positionName}`;

    setAiScoring(true);
    setAiScoreProgress(0);
    setAiScoringTotal(total);
    setAiScores({});
    setAiSortActive(false);
    const newScores: Record<string, number> = {};
    for (let i = 0; i < total; i++) {
      const c = snapshot[i];
      try {
        const res = await aiAnalyzeResume(c.id, false, posJd || undefined);
        const p = res?.parsed ?? {};
        const score = p.jd_match_score ?? 0;
        newScores[c.id] = score;
        setAiScores({ ...newScores });
        // Auto-write AI conclusion to comment
        if (score > 0 || p.summary) {
          const lines: string[] = [`🤖 AI批量评分 · 岗位匹配度 ${score}/10`];
          if (p.jd_match_summary) lines.push(p.jd_match_summary);
          if (p.jd_risks?.length) lines.push(`⚠️ 风险：${p.jd_risks.join('；')}`);
          if (p.summary) lines.push(`简历摘要：${p.summary}`);
          if (p.strengths?.length) lines.push(`✅ 优势：${p.strengths.join('、')}`);
          createEvaluation({ candidate_id: c.id, comment: lines.join('\n'), stage: '', rating: 0, result: 'pending' }).catch(() => {});
        }
      } catch {
        newScores[c.id] = 0;
      }
      setAiScoreProgress(i + 1);
    }
    setAiScoring(false);
    setAiSortActive(true);
    toast.success(`AI评分完成，共 ${total} 位候选人`);
  };

  const handleBatchAssign = async (positionId: string) => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    try {
      const res = await batchAssignCandidates(ids, positionId);
      toast.success(res.message || '分配成功');
      exitSelectMode();
      onUpdate?.();
    } catch (err: any) {
      toast.error(err.response?.data?.message || '分配失败');
    }
  };

  const handleFeishuNotify = () => {
    const selected = candidates.filter(c => selectedIds.has(c.id));
    if (selected.length === 0) return;
    onFeishuNotify?.(selected);
    exitSelectMode();
  };

  const switchTab = (groupKey: string) => {
    setActiveGroupKey(groupKey);
    // 默认选中第一个子分类(如「待筛选」)，优先展示待处理的候选人，而非全部CV
    const group = DISPLAY_STAGE_GROUPS.find(g => g.key === groupKey);
    setActiveSubCategory(group?.subCategories?.[0]?.stage ?? null);
    setSelectedIds(new Set());
  };

  // Notify parent of current stage's candidate IDs whenever stage or candidates change
  React.useEffect(() => {
    if (onStageChange) {
      onStageChange(currentItems.map(c => c.id));
    }
  }, [activeGroupKey, candidates, onStageChange]);

  // Seed aiScores from persisted jd_match_score on candidates
  React.useEffect(() => {
    const persisted: Record<string, number> = {};
    for (const c of candidates) {
      if (c.jd_match_score && c.jd_match_score > 0) {
        persisted[c.id] = c.jd_match_score;
      }
    }
    if (Object.keys(persisted).length > 0) {
      setAiScores(prev => ({ ...persisted, ...prev }));
    }
  }, [candidates]);

  const handleAiSearch = async () => {
    if (!aiQuery.trim() || aiLoading) return;
    setAiLoading(true);
    setAiAnswer('');
    setAiCandidates([]);
    setAiOpen(true);
    try {
      const data = await aiSearch(aiQuery.trim());
      setAiAnswer(data.answer || '未获取到结果');
      setAiCandidates(data.candidates || []);
    } catch (err: any) {
      setAiAnswer(err.response?.data?.message || 'AI搜索出错，请稍后重试');
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <div className="h-full flex flex-col gap-2.5">
      {/* Header with title + AI search + feishu button */}
      <div className="px-3 md:px-5 pt-3 pb-2 bg-white rounded-2xl border border-gray-200/80 shrink-0">
        <div className="flex flex-wrap items-center justify-between mb-3 gap-2">
          <div className="shrink-0 pl-10 md:pl-0">
            <h1 className="text-lg font-bold text-gray-800">
              {showReusableOnly ? '回捞池' : showFavoritesOnly ? '我的收藏' : (positionName || '人才池')}
            </h1>
            <p className="text-sm text-gray-400 mt-0.5">{showReusableOnly ? `${currentItems.length} 位可再次激活的淘汰候选人` : showFavoritesOnly ? `${currentItems.length} 位收藏候选人` : `${candidates.length} 位候选人`}</p>
          </div>

          {/* Unified Search Box */}
          <form onSubmit={handleSearch} className="hidden md:block flex-1 max-w-[500px] mx-4 relative">
            <div className="relative flex items-center">
              {/* Mode indicator icon */}
              {aiLoading ? (
                <Loader2 size={14} className="absolute left-3 text-primary-400 pointer-events-none animate-spin" />
              ) : localSearch && !isKeywordQuery(localSearch) ? (
                <Sparkles size={14} className="absolute left-3 text-primary-400 pointer-events-none" />
              ) : (
                <Search size={14} className="absolute left-3 text-gray-400 pointer-events-none" />
              )}
              <input
                ref={aiInputRef}
                value={localSearch}
                onChange={e => setLocalSearch(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleUnifiedSearch(localSearch); } }}
                onFocus={() => { if (aiAnswer) setAiOpen(true); }}
                placeholder="搜索候选人：手机号、姓名、邮箱，或描述如「3年嵌入式经验」"
                className="w-full h-9 pl-9 pr-24 rounded-xl border border-gray-200 bg-gray-50/80 text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 focus:bg-white transition-all"
              />
              <div className="absolute right-1.5 flex items-center gap-1">
                {localSearch && (
                  <button type="button" onClick={clearSearch} className="p-1 text-gray-400 hover:text-gray-600 rounded transition-colors">
                    <X size={13} />
                  </button>
                )}
                <button
                  type="submit"
                  disabled={!localSearch.trim() || aiLoading}
                  className="px-2.5 py-1 text-[11px] font-medium text-white rounded-lg transition-colors disabled:opacity-40 flex items-center gap-1
                    " style={{ background: localSearch && !isKeywordQuery(localSearch) ? '#101828' : '#2e6bf0' }}
                >
                  {aiLoading ? <Loader2 size={12} className="animate-spin" /> : localSearch && !isKeywordQuery(localSearch) ? <Sparkles size={11} /> : <Zap size={11} />}
                  搜索
                </button>
              </div>
            </div>

            {/* AI Results Dropdown */}
            {aiOpen && (aiAnswer || aiLoading) && (
              <div className="absolute top-full left-0 right-0 mt-1.5 z-50 bg-white rounded-xl border border-gray-200 shadow-xl overflow-hidden max-h-[520px] flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 bg-gradient-to-r from-primary-50 to-primary-50 shrink-0">
                  <div className="flex items-center gap-2">
                    <Sparkles size={13} className="text-primary-500" />
                    <span className="text-xs font-medium text-gray-600">AI 智能搜索</span>
                    {aiCandidates.length > 0 && (
                      <span className="px-1.5 py-0.5 rounded-full bg-primary-100 text-primary-600 text-[10px] font-medium">
                        {aiCandidates.length} 人匹配
                      </span>
                    )}
                  </div>
                  <button onClick={() => setAiOpen(false)} className="p-0.5 text-gray-400 hover:text-gray-600 rounded">
                    <X size={14} />
                  </button>
                </div>

                <div className="overflow-y-auto flex-1">
                  {/* Loading state */}
                  {aiLoading && (
                    <div className="flex items-center justify-center gap-2 py-10 text-gray-400">
                      <Loader2 size={18} className="animate-spin text-primary-400" />
                      <span className="text-sm">AI 正在分析 {candidates.length} 位候选人...</span>
                    </div>
                  )}

                  {/* AI Answer */}
                  {!aiLoading && aiAnswer && (
                    <div className="px-4 py-3">
                      <div className="text-[13px] text-gray-700 leading-relaxed whitespace-pre-wrap ai-answer">
                        {aiAnswer.split(/(\*\*[^*]+\*\*)/).map((part, i) =>
                          /^\*\*[^*]+\*\*$/.test(part)
                            ? <strong key={i} className="text-gray-900">{part.replace(/\*\*/g, '')}</strong>
                            : <span key={i}>{part.replace(/\[ID:[a-f0-9-]+\]/gi, '')}</span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Matched candidate cards */}
                  {!aiLoading && aiCandidates.length > 0 && (
                    <div className="px-3 pb-3 space-y-1.5">
                      <p className="text-[10px] text-gray-400 font-medium px-1 pt-1">点击查看候选人详情</p>
                      {aiCandidates.map((c: any) => (
                        <div
                          key={c.id}
                          onClick={() => { onSelect(c.id); setAiOpen(false); }}
                          className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-gray-100 hover:border-primary-200 hover:bg-primary-50/30 cursor-pointer transition-all group"
                        >
                          <div className="shrink-0 w-9 h-9 rounded-full bg-gray-100 overflow-hidden flex items-center justify-center">
                            {c.photo_url ? (
                              <img src={c.photo_url} alt={c.name} className="w-full h-full object-cover" />
                            ) : (
                              <User size={16} className="text-gray-400" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-semibold text-gray-800 group-hover:text-primary-700 truncate">{c.name}</span>
                              {c.stage && (
                                <span className={clsx(
                                  'px-1.5 py-0.5 rounded text-[10px] font-medium',
                                  c.stage === 'offer' || c.stage === 'pending_onboard'
                                    ? 'bg-[#CDE8D9] text-emerald-700'
                                    : 'bg-gray-100 text-gray-500'
                                )}>
                                  {STAGE_LABELS[c.stage as CandidateStage] || c.stage}
                                </span>
                              )}
                            </div>
                            <p className="text-[11px] text-gray-500 mt-0.5 truncate">
                              {[
                                c.age > 0 && `${c.age}岁`,
                                c.education,
                                c.school,
                                c.work_years > 0 && `${c.work_years}年经验`,
                                c.latest_company,
                                c.latest_title,
                                c.expected_city,
                              ].filter(Boolean).join(' · ')}
                            </p>
                          </div>
                          {c.position_name && (
                            <span className="shrink-0 text-[10px] text-primary-600 font-medium bg-primary-50 px-1.5 py-0.5 rounded-full">
                              {c.position_name}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </form>

          <div className="flex items-center gap-2 shrink-0 pr-0">
            {selectMode ? (
              <>
                <button onClick={selectAll}
                  className="px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                  {selectedIds.size === currentItems.length && currentItems.length > 0 ? '取消全选' : `全选本页(${currentItems.length})`}
                </button>
                {isHr && positions && positions.filter(p => p.status === 'open').length > 0 && (
                  <div className="relative" ref={assignRef}>
                    <button onClick={() => setShowAssignDropdown(!showAssignDropdown)} disabled={selectedIds.size === 0}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-primary-700 bg-primary-50 hover:bg-primary-100 rounded-lg transition-colors disabled:opacity-40">
                      <FolderInput size={12} />分配岗位（{selectedIds.size}）
                    </button>
                    {showAssignDropdown && selectedIds.size > 0 && (
                      <div className="absolute right-0 top-full mt-1 w-56 bg-white rounded-xl border border-gray-200 shadow-xl z-50 max-h-72 overflow-y-auto py-1">
                        <p className="px-3 py-1.5 text-[10px] text-gray-400 font-medium">选择目标岗位</p>
                        {positions.filter(p => p.status === 'open').map(p => (
                          <button key={p.id} onClick={() => { handleBatchAssign(p.id); setShowAssignDropdown(false); }}
                            className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-gray-50 transition-colors">
                            <Briefcase size={12} className="text-gray-400 shrink-0" />
                            <span className="text-xs text-gray-700 truncate flex-1">{p.name}</span>
                            <span className="text-[10px] text-gray-400 shrink-0">{p.candidate_count || 0}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <button onClick={handleFeishuNotify} disabled={selectedIds.size === 0}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors disabled:opacity-40">
                  <Send size={12} />飞书评审（{selectedIds.size}）
                </button>
                <button onClick={exitSelectMode} className="px-3 py-1.5 text-xs text-gray-500 hover:bg-gray-100 rounded-lg">取消</button>
              </>
            ) : (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
                  className={clsx(
                    'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors',
                    showFavoritesOnly
                      ? 'text-amber-600 bg-amber-50 hover:bg-amber-100'
                      : 'text-gray-500 bg-gray-50 hover:bg-gray-100'
                  )}
                  title="只看我收藏的候选人"
                >
                  <Star size={12} fill={showFavoritesOnly ? 'currentColor' : 'none'} />
                  我的收藏{favSet.size > 0 ? `（${favSet.size}）` : ''}
                </button>
                {isHr && (
                  <button
                    onClick={() => setShowReusableOnly(!showReusableOnly)}
                    className={clsx(
                      'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors',
                      showReusableOnly
                        ? 'text-emerald-700 bg-emerald-50 hover:bg-emerald-100'
                        : 'text-gray-500 bg-gray-50 hover:bg-gray-100'
                    )}
                    title="因时间/薪资/地点/主动放弃等非技术原因淘汰的候选人，未来可再次合作"
                  >
                    <UserCheck size={12} />
                    回捞池
                  </button>
                )}
                {isHr && (
                  <button
                    onClick={handleBatchAiScore}
                    disabled={aiScoring || currentItems.length === 0}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-primary-600 bg-primary-50 hover:bg-primary-100 rounded-lg transition-colors disabled:opacity-50"
                  >
                    {aiScoring ? (
                      <><Loader2 size={12} className="animate-spin" />{aiScoreProgress}/{aiScoringTotal}</>  
                    ) : (
                      <><BarChart2 size={12} />AI评分{aiSortActive ? '（已排序）' : ''}</>
                    )}
                  </button>
                )}
                {aiSortActive && (
                  <button onClick={() => { setAiSortActive(false); setAiScores({}); }}
                    className="px-2 py-1.5 text-xs text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                    <X size={12} />
                  </button>
                )}
                {isHr && onFeishuNotify && (
                  <button onClick={() => setSelectMode(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-primary-600 bg-primary-50 hover:bg-primary-100 rounded-lg transition-colors">
                    <Send size={12} />批量操作
                  </button>
                )}
              </div>
            )}
            {notificationBell}
          </div>
        </div>

        {/* Stage tabs - scrollable on mobile */}
        <div className="flex items-stretch gap-1 pb-3 overflow-x-auto no-scrollbar">
          {DISPLAY_STAGE_GROUPS.map(group => {
            const count = group.stages.reduce((sum, stage) => sum + (grouped[stage] || []).length, 0);
            const isActive = activeGroupKey === group.key;
            const primaryStage = group.stages[0] as CandidateStage;
            return (
              <button
                key={group.key}
                onClick={() => switchTab(group.key)}
                className={clsx(
                  'flex-1 min-w-[60px] md:min-w-0 flex flex-col items-center justify-center gap-0.5 md:gap-1 py-2 md:py-2.5 rounded-xl transition-all duration-200 ease-out border',
                  isActive
                    ? `${stageActiveColors[primaryStage]} shadow-sm border-black/[0.04]`
                    : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50/60 border-transparent'
                )}
              >
                <div className="flex items-center gap-1">
                  <span className={clsx(
                    'w-[5px] h-[5px] rounded-full transition-all duration-200 hidden md:block',
                    isActive ? stageDotColors[primaryStage] : 'bg-gray-300'
                  )} />
                  <span className="text-[10px] md:text-[12px] font-medium whitespace-nowrap">{group.label}</span>
                </div>
                <span className={clsx(
                  'text-sm md:text-base font-bold tabular-nums transition-all duration-200',
                  isActive
                    ? stageCountColors[primaryStage].split(' ').filter(c => c.startsWith('text-')).join(' ')
                    : 'text-gray-300'
                )}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Sub-categories (if exists) */}
        {activeGroup.subCategories && (
          <div className="flex items-center gap-2 pb-2 px-1">
            {activeGroup.subCategories.map(sub => {
              const count = (grouped[sub.stage] || []).length;
              return (
                <button
                  key={sub.key}
                  onClick={() => setActiveSubCategory(sub.stage)}
                  className={clsx(
                    'px-3 py-1.5 rounded-lg text-xs font-medium transition-all',
                    activeSubCategory === sub.stage
                      ? 'bg-gray-800 text-white shadow-sm'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  )}
                >
                  {sub.label} ({count})
                </button>
              );
            })}
            <button
              onClick={() => setActiveSubCategory(null)}
              className={clsx(
                'px-3 py-1.5 rounded-lg text-xs font-medium transition-all',
                activeSubCategory === null
                  ? 'bg-gray-800 text-white shadow-sm'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              )}
            >
              全部 ({activeGroup.stages.reduce((sum, stage) => sum + (grouped[stage] || []).length, 0)})
            </button>
          </div>
        )}
      </div>

      {/* Candidate list */}
      {currentItems.length === 0 ? (
        <div className="flex flex-col items-center justify-center flex-1 text-gray-400">
          <Users size={40} strokeWidth={1} className="text-gray-300" />
          <p className="mt-3 text-sm">{showFavoritesOnly ? '还没有收藏的候选人，点击候选人右侧的星标即可收藏' : `「${activeGroup.label}」暂无候选人`}</p>
        </div>
      ) : (
        <div className="flex-1 min-h-0 overflow-y-auto">
          <div className="flex flex-col gap-2 md:gap-2.5">
            {currentItems.map(c => {
              const infoParts: string[] = [];
              if (c.age > 0) infoParts.push(`${c.age}岁`);
              if (c.education) infoParts.push(c.education);
              if (c.work_years > 0) infoParts.push(`${c.work_years}年`);
              if (c.expected_city) infoParts.push(c.expected_city);

              // Use work_experiences array, fallback to latest_company/latest_title
              const rawExps = (c.work_experiences && c.work_experiences.length > 0)
                ? c.work_experiences.slice(0, 3)
                : (c.latest_company || c.latest_title)
                  ? [{ company: c.latest_company, title: c.latest_title }]
                  : [];
              const projs = (c.project_experiences && c.project_experiences.length > 0)
                ? c.project_experiences.slice(0, 3) : [];
              // Prefer project experiences when work experiences are weak
              const weakWork = rawExps.length <= 1 && rawExps.every(e => !e.title && (!e.company || e.company.length < 6));
              const exps = (weakWork && projs.length > 0) ? [] : rawExps;

              return (
                <div
                  key={c.id}
                  onClick={() => selectMode ? toggleSelect(c.id) : onSelect(c.id)}
                  className={clsx(
                    'bg-white rounded-xl border px-3 md:px-5 py-3 md:py-3.5 cursor-pointer transition-all duration-150 flex items-center',
                    'hover:shadow-md hover:border-primary-200',
                    selectMode && selectedIds.has(c.id) && 'border-primary-400 bg-primary-50/50 shadow-md ring-1 ring-primary-200',
                    !selectMode && 'border-gray-200 shadow-sm'
                  )}
                >
                  <div className="flex gap-3 md:gap-4 items-center w-full">
                    {/* Checkbox */}
                    {selectMode && (
                      <div className="shrink-0">
                        {selectedIds.has(c.id)
                          ? <CheckSquare size={18} className="text-primary-600" />
                          : <Square size={18} className="text-gray-300" />
                        }
                      </div>
                    )}

                    {/* LEFT: Avatar + name + info + tags */}
                    <div className="shrink-0 md:w-[220px] flex items-center gap-2.5 md:gap-3 pointer-events-none">
                      <div className="shrink-0 w-10 h-10 md:w-11 md:h-11 rounded-full bg-gray-100 overflow-hidden flex items-center justify-center">
                        {c.photo_url ? (
                          <img src={c.photo_url} alt={c.name} className="w-full h-full object-cover" />
                        ) : (
                          <User size={20} className="text-gray-400" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                          <h3 className="text-[13px] md:text-[14px] font-bold text-gray-900 truncate leading-5">{c.name}</h3>
                          <span className={clsx(
                            'px-1.5 py-0.5 rounded text-[10px] font-medium shrink-0',
                            c.stage === 'offer' || c.stage === 'pending_onboard'
                              ? 'bg-[#CDE8D9] text-emerald-700'
                              : 'bg-gray-100 text-gray-500'
                          )}>
                            {STAGE_LABELS[c.stage as CandidateStage]}
                          </span>
                          {aiScores[c.id] !== undefined && (
                            <span className="relative shrink-0 flex items-center gap-0.5">
                              <button
                                onClick={e => { e.stopPropagation(); setFeedbackOpen(feedbackOpen === c.id ? null : c.id); }}
                                className={clsx(
                                  'px-1.5 py-0.5 rounded text-[10px] font-bold transition-colors',
                                  aiScores[c.id] >= 8 ? 'bg-primary-100 text-primary-700 hover:bg-primary-200' :
                                  aiScores[c.id] >= 6 ? 'bg-gray-100 text-gray-700 hover:bg-gray-200' :
                                  'bg-gray-50 text-gray-500 hover:bg-gray-100'
                                )}
                                title="点击反馈AI评分是否准确">
                                ⭐{aiScores[c.id]}/10
                              </button>
                              {feedbackOpen === c.id && (
                                <span className="flex items-center gap-0.5 ml-0.5" onClick={e => e.stopPropagation()}>
                                  {([['agree','✓','认可','bg-primary-100 text-primary-700'],['too_high','↓','偏高','bg-gray-100 text-gray-600'],['too_low','↑','偏低','bg-primary-100 text-primary-600']] as const).map(([verdict, icon, label, cls]) => (
                                    <button key={verdict} title={label}
                                      className={`px-1 py-0.5 rounded text-[9px] font-bold ${cls} hover:opacity-80`}
                                      onClick={async () => {
                                        setFeedbackOpen(null);
                                        try {
                                          await aiScoreFeedback({ candidate_id: c.id, position_id: currentPositionId, ai_score: aiScores[c.id], hr_verdict: verdict });
                                          toast.success(`已记录：${label}`);
                                        } catch { toast.error('反馈提交失败'); }
                                      }}>{icon}{label}</button>
                                  ))}
                                </span>
                              )}
                            </span>
                          )}
                        </div>
                        {c.tags && c.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-0.5">
                            {c.tags.slice(0, 3).map(t => (
                              <span key={t.id} className="px-1.5 py-0 rounded text-[10px] font-medium leading-4 whitespace-nowrap"
                                style={{ backgroundColor: t.color + '15', color: t.color, border: `1px solid ${t.color}30` }}>
                                {t.name}
                              </span>
                            ))}
                            {c.tags.length > 3 && <span className="text-[10px] text-gray-400">+{c.tags.length - 3}</span>}
                          </div>
                        )}
                        <p className="text-[11px] text-gray-500 mt-0.5 leading-4 truncate">
                          {infoParts.length > 0 ? infoParts.join(' · ') : '\u00A0'}
                        </p>
                        {/* Mobile: show first work exp inline */}
                        {exps.length > 0 && (
                          <p className="md:hidden text-[11px] text-gray-500 mt-0.5 truncate">
                            {exps[0].company || ''}{exps[0].company && exps[0].title ? ' · ' : ''}{exps[0].title || ''}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* CENTER: Work experiences - hidden on mobile */}
                    <div className="hidden md:block flex-1 min-w-0 pointer-events-none">
                      {exps.length > 0 ? (
                        <div className="space-y-1.5">
                          {exps.slice(0, 3).map((exp, idx) => (
                            <div key={idx} className="flex items-center gap-1.5 min-w-0">
                              <Briefcase size={12} className={idx === 0 ? 'text-gray-500 shrink-0' : 'text-gray-300 shrink-0'} />
                              <p className={clsx(
                                'text-[12px] truncate leading-4',
                                idx === 0 ? 'text-gray-800 font-medium' : 'text-gray-500'
                              )}>
                                {exp.company || ''}{exp.company && exp.title ? ' · ' : ''}{exp.title || ''}
                              </p>
                            </div>
                          ))}
                        </div>
                      ) : projs.length > 0 ? (
                        <div className="space-y-1.5">
                          {projs.map((proj, idx) => (
                            <div key={idx} className="flex items-center gap-1.5 min-w-0">
                              <FolderOpen size={12} className={idx === 0 ? 'text-primary-400 shrink-0' : 'text-gray-300 shrink-0'} />
                              <p className={clsx(
                                'text-[12px] truncate leading-4',
                                idx === 0 ? 'text-gray-700 font-medium' : 'text-gray-500'
                              )}>
                                {proj}
                              </p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5 text-gray-300">
                          <Briefcase size={12} />
                          <span className="text-[11px]">暂无工作经历</span>
                        </div>
                      )}
                    </div>

                    {/* Education - hidden on mobile */}
                    <div className="hidden md:block shrink-0 w-[200px] pointer-events-none">
                      {c.school ? (
                        <div className="flex items-center gap-1.5">
                          <GraduationCap size={12} className="text-gray-400 shrink-0" />
                          <p className="text-[12px] text-gray-800 font-medium truncate leading-4">
                            {c.school}{c.education ? ` · ${c.education}` : ''}
                          </p>
                        </div>
                      ) : c.education ? (
                        <div className="flex items-center gap-1.5">
                          <GraduationCap size={12} className="text-gray-400 shrink-0" />
                          <span className="text-[12px] text-gray-600 leading-4">{c.education}</span>
                        </div>
                      ) : null}
                    </div>

                    {/* RIGHT: Position badge - hidden on mobile */}
                    <div className="hidden md:flex shrink-0 w-[80px] flex-col items-end gap-1 pointer-events-none">
                      {!positionName && (
                        c.position_name ? (
                          <span className="inline-flex items-center gap-0.5 text-[10px] text-primary-600 font-medium bg-primary-50 px-1.5 py-0.5 rounded-full truncate max-w-full">
                            <Building2 size={10} />{c.position_name}
                          </span>
                        ) : (
                          <span className="text-[10px] text-gray-500 font-medium bg-gray-50 px-1.5 py-0.5 rounded-full">在野</span>
                        )
                      )}
                      {c.source && (
                        <span className="text-[10px] text-gray-400 truncate max-w-full">{c.source}</span>
                      )}
                    </div>

                    {/* Favorite star */}
                    <button
                      onClick={e => { e.stopPropagation(); toggleFavorite(c.id); }}
                      className={clsx(
                        'shrink-0 p-1.5 rounded-lg transition-colors',
                        favSet.has(c.id) ? 'text-amber-500 hover:bg-amber-50' : 'text-gray-300 hover:text-amber-500 hover:bg-amber-50'
                      )}
                      title={favSet.has(c.id) ? '取消收藏' : '收藏候选人'}
                    >
                      <Star size={16} fill={favSet.has(c.id) ? 'currentColor' : 'none'} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
